import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * Retire du <head> les balises posées par le SERVEUR (attribut `data-ssr-seo`,
 * cf. public/index.html et seo-inject.js) dès que SEO.js a posé la sienne.
 *
 * Avec React 19, react-helmet-async laisse React ajouter ses balises sans
 * retirer celles du HTML servi : mesuré en production le 20/10/2026, la page
 * rendue portait deux meta description et, sur les alias de catégorie, deux
 * canoniques contradictoires — ce que Google demande d'éviter (« Don't specify
 * different URLs as canonical for the same page »,
 * developers.google.com/search/docs/crawling-indexing/consolidate-duplicate-urls).
 * Une balise serveur n'est retirée QUE si une balise non marquée de même clé
 * existe : si SEO.js n'en émet pas (canonique absente), celle du serveur reste.
 * robots/googlebot ne sont jamais marquées, donc jamais retirées.
 */
export function retirerDoublonsServeur(head = typeof document !== "undefined" ? document.head : null) {
  if (!head) return;
  const cle = (n) => (n.tagName === "LINK"
    ? `link|${n.getAttribute("rel")}`
    : `meta|${n.getAttribute("name") || ""}|${n.getAttribute("property") || ""}`);
  const remplacees = new Set(
    [...head.querySelectorAll("meta:not([data-ssr-seo]), link[rel='canonical']:not([data-ssr-seo])")].map(cle),
  );
  head.querySelectorAll("[data-ssr-seo]").forEach((n) => {
    if (remplacees.has(cle(n))) n.remove();
  });
}

// Phase 13.37 — Lucas : "qui ecrit des trucs comme ca... c'est pas
// commercial". Aucun taux ni montant de contribution dans la default
// description : la contribution solidaire payée par l'acheteur (montant servi
// par le serveur) reste expliquée sur /comment-ca-marche et dans le tunnel
// d'achat — pas dans la 1re impression Google.
const DEFAULT_DESCRIPTION =
  "Vendez ou louez vos objets, proposez des services solidaires sur Gively, et versez la part de votre choix à une association. 100 % français, paiement sécurisé.";

const DEFAULT_KEYWORDS =
  "marketplace solidaire, vente solidaire, don association, seconde main, "
  + "association partenaire, financement association, vendre pour une cause, "
  + "économie circulaire, vente entre particuliers, location entre particuliers, "
  + "louer, services solidaires, entraide";

/**
 * Composant SEO unifié — wrapper autour de react-helmet-async.
 *
 * Fournit en une seule balise tous les meta-tags critiques pour le
 * référencement : title, description, canonical, Open Graph, Twitter
 * Card, robots, et autant de blocs JSON-LD que nécessaire.
 *
 * Props :
 *   title        : string (sans suffixe Gively, on l'ajoute auto)
 *   description  : string ≤ 160 caractères recommandé
 *   image        : URL absolue d'une OG image 1200×630
 *   url / path   : URL absolue OU path (on construit la canonical)
 *   type         : "website" | "article" | "product" | …
 *   noindex      : bool — vrai pour les pages privées (admin, checkout) ;
 *                  null = aucune balise robots, le SSR décide (pages programmatiques)
 *   keywords     : string libre (best-effort, peu utilisé par Google
 *                  mais lu par Bing et autres moteurs FR)
 *   jsonLd       : objet OU tableau d'objets Schema.org
 *
 * Usage typique :
 *   <SEO title="Vendre" description="..." path="/vendre" jsonLd={{...}} />
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  // 03/11/2026 — nouvelle image de partage (variante E choisie par Lucas) ; « ?v= »
  // force WhatsApp / SMS / Facebook à relire l'image au lieu de l'ancienne en cache.
  image = "https://www.gively.fr/og-image.jpg?v=20261103",
  url,
  path,
  type = "website",
  noindex = false,
  keywords = DEFAULT_KEYWORDS,
  jsonLd,
}) => {
  const safeTitle = title || "Gively — Marketplace solidaire française";
  const fullTitle = safeTitle.includes("Gively") ? safeTitle : `${safeTitle} | Gively`;
  const canonical = url || (path ? `https://www.gively.fr${path}` : "");

  // Indication explicite à Googlebot pour optimiser l'affichage des
  // rich snippets : taille max snippet 200, image preview large,
  // vidéo preview large. Sans ces hints, Google peut tronquer
  // les snippets de manière conservatrice.
  // noindex,FOLLOW (audit SEO 06/08/2026 P2-5) : on sort la page de l'index
  // sans casser la circulation du maillage interne (nofollow gaspillait le
  // signal des liens sortants des pages privées).
  // noindex === null : LE SERVEUR DÉCIDE (audit d'acquisition du 10/10/2026, lot A).
  // Les pages programmatiques (catégorie, ville, cause, département) reçoivent
  // leur balise robots du SSR (frontend/server.js ← /api/seo/meta ← source unique
  // backend/services/seo_indexability). Cette balise reste dans le HTML après
  // l'hydratation ; une seconde balise posée ici s'y AJOUTE, et Google retient la
  // plus restrictive. Un seuil côté navigateur (3) différent de celui du serveur
  // (2) mettait ainsi en noindex des pages que le sitemap soumettait, et un échec
  // d'API faisait de même. On n'émet donc rien.
  const robotsContent = noindex === null
    ? null
    : noindex
      ? "noindex,follow"
      : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";

  useEffect(() => { retirerDoublonsServeur(); });

  return (
    <Helmet>
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {robotsContent && <meta name="robots" content={robotsContent} />}
      {robotsContent && <meta name="googlebot" content={robotsContent} />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content="Gively" />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* JSON-LD Schema.org — un ou plusieurs blocs au choix. */}
      {jsonLd && !Array.isArray(jsonLd) && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
      {jsonLd && Array.isArray(jsonLd) && jsonLd.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
};

/**
 * Helper — génère un bloc BreadcrumbList Schema.org compatible
 * Google Rich Results. À passer dans le tableau `jsonLd` du composant
 * SEO. Affiche un fil d'ariane dans les SERP, +CTR mesuré entre 5 et 12 %.
 *
 * Usage :
 *   const crumbs = breadcrumbsJsonLd([
 *     { name: "Accueil", path: "/" },
 *     { name: "Associations", path: "/associations" },
 *   ]);
 *   <SEO jsonLd={[pageJsonLd, crumbs]} />
 */
const SITE_BASE = "https://www.gively.fr";

/**
 * Emplacement d'une miette, ou `undefined` si on ne le connaît pas.
 *
 * DÉFAUT CORRIGÉ LE 31/08/2026 (lot X). Ce helper ne lisait que `it.path`,
 * mais deux pages piliers lui passaient `{ name, url }`. `it.path` valait donc
 * `undefined`, et l'expression de repli
 *
 *     `https://www.gively.fr${it.path || ""}`
 *
 * FABRIQUAIT l'URL de la page d'accueil — pour CHAQUE miette. Le fil d'Ariane
 * servi à Google annonçait donc « Accueil » et « Vendre en toute sécurité »
 * pointant tous deux vers la racine du site. Vérifié en exécutant le helper
 * sur la forme réellement passée, pas en le lisant.
 *
 * Deux corrections, et la seconde compte plus que la première :
 *   1. on accepte `path` ET `url` — la divergence de nom ne peut plus nuire ;
 *   2. quand on ne sait TOUJOURS pas, on rend `undefined` et l'appelant OMET
 *      la propriété. Google autorise explicitement un `ListItem` sans `item`
 *      (« The last breadcrumb in the list should not have this property » —
 *      developers.google.com/search/docs/appearance/structured-data/breadcrumb,
 *      consultée le 31/08/2026) ; il n'autorise nulle part une URL FAUSSE, et
 *      sa politique impose l'inverse (« must be a true representation of the
 *      page content »). Une miette sans emplacement est honnête ; une miette
 *      qui pointe la mauvaise page est un mensonge — même doctrine que les
 *      écrans, où une panne ne s'affiche jamais comme « 0 ».
 */
const emplacementMiette = (it) => {
  const brut = it.path ?? it.url;
  if (typeof brut !== "string" || brut === "") return undefined;
  return brut.startsWith("http") ? brut : `${SITE_BASE}${brut}`;
};

export const breadcrumbsJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => {
    const item = emplacementMiette(it);
    return {
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      ...(item ? { item } : {}),
    };
  }),
});

/**
 * Helper — bloc Organization Schema.org de Gively (landings stratégiques).
 *
 * 11/10/2026 (audit Google) : plus d'AggregateRating. Les notes de la
 * plateforme sont des notes ENTRE utilisateurs (acheteur ↔ vendeur), pas des
 * avis sur Gively ; et Google écrit : « If the entity that's being reviewed
 * controls the reviews about itself, their pages that use LocalBusiness or any
 * other type of Organization structured data are ineligible for star review
 * feature » (developers.google.com/search/docs/appearance/structured-data/
 * review-snippet, page du 2026-09-08). Le bloc ne promettait donc aucune étoile,
 * et attribuait à Gively des notes qui ne la concernaient pas.
 */
export const organizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Gively",
  "alternateName": "Marketplace Solidaire",
  "url": "https://www.gively.fr",
  "logo": "https://www.gively.fr/icon-512x512.png",
  "description": "Marketplace solidaire française — chaque vente, location ou service peut générer un don automatique à l'association choisie par le vendeur.",
  "address": { "@type": "PostalAddress", "addressLocality": "Toulouse", "addressCountry": "FR" },
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "contact@gively.fr",
    "contactType": "customer service",
    "availableLanguage": ["French"],
  },
  // Alignés sur le bloc Organization d'index.html (source des VRAIS profils —
  // audit SEO 06/08/2026 P1-5 : trois définitions divergentes brouillaient le
  // Knowledge Graph ; une seule identité d'entité désormais).
  "sameAs": [
    "https://www.instagram.com/gively.fr",
    "https://www.facebook.com/share/1D1dQsnkLa/",
    "https://www.linkedin.com/company/assomart/",
    "https://www.facebook.com/gively.fr",
    "https://play.google.com/store/apps/details?id=com.gively.app",
    "https://apps.apple.com/fr/app/gively-marketplace-solidaire/id6788333825",
  ],
});

/**
 * Helper — génère un bloc FAQPage Schema.org qui décrit la FAQ VISIBLE de la page.
 * Aucune promesse d'affichage : Google a annoncé que le résultat enrichi FAQ
 * « will no longer appear in Google Search starting May 7, 2026 »
 * (developers.google.com/search/updates, relevé par l'audit du 10/10/2026). Le
 * balisage reste valide ; on n'en ajoute pas de nouveau pour un gain de clics.
 *
 * Usage :
 *   const faq = faqJsonLd([
 *     { q: "Comment ça marche ?", a: "Vendez puis choisissez..." },
 *     { q: "Combien ça coûte ?", a: "L'inscription est gratuite..." },
 *   ]);
 */
export const faqJsonLd = (qaPairs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qaPairs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export default SEO;
